import React from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../components/Menu';
import { TopicPrivacy } from '../graphql/generated/graphql';
import editIcon from '../icons/non-prefilled/16x16/editPost.svg';
import favoriteIcon from '../icons/non-prefilled/16x16/favorite.svg';
import memberIcon from '../icons/non-prefilled/16x16/member.svg';
import reorderIcon from '../icons/non-prefilled/16x16/reorder.svg';
import topicIcon from '../icons/non-prefilled/16x16/topic.svg';
import CreatePostMenuItem from '../post/CreatePostMenuItem';
import EditTopicModal from '../topic/modal/EditTopicModal';
import NewTopicModal from '../topic/modal/NewTopicModal';
import topicPolicyCheck, { TopicPolicyAction } from '../topic/topicPolicyCheck';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import useToggleFavoriteTopic from '../topic/useToggleFavoriteTopic';
import TopicUsersPanel from '../topic/users/TopicUsersPanel';
import { UIActions } from '../ui/uiSlice';
import { useRouter } from '../utils/router/v3Bridge';
import topicPath from '../utils/url/topicPath';
import { usePublicContentPermissions } from '../utils/useAllowPublicContentBy';
import ReorderTopicsModal from './ReorderTopicsModal';
import { isFeatureEnabled } from '../utils/features';

interface TopicItemMenuItemsProps {
  topic: AllTopicsTreeListTopic;
}

const TopicItemMenuItems = ({ topic }: TopicItemMenuItemsProps) => {
  const router = useRouter();
  const currentUser = useAppSelector((state) => state.session.user);
  const { isFavorited, toggleFavoriteTopic } = useToggleFavoriteTopic(topic);
  const dispatch = useAppDispatch();

  const { isPermittedToCreatePublicSubtopic } = usePublicContentPermissions();

  const canEditTopic = topicPolicyCheck(
    TopicPolicyAction.EditTopic,
    topic,
    currentUser,
  );

  const handleNewSubtopic =
    topicPolicyCheck(TopicPolicyAction.AddSubtopic, topic, currentUser) &&
    (() =>
      dispatch(UIActions.openModal(NewTopicModal, { parentId: topic.id })));

  const handleEditTopic =
    canEditTopic &&
    (() => dispatch(UIActions.openModal(EditTopicModal, { topic })));

  const handleViewMembers = () => {
    router.push(topicPath(topic));
    setTimeout(() => dispatch(UIActions.openRightPanel(TopicUsersPanel)), 0);
  };

  const handleReorderSubtopics = () => {
    dispatch(
      UIActions.openModal(ReorderTopicsModal, {
        topics: topic.childTopics,
        parentTopic: topic,
      }),
    );
  };

  const canCreatePost = topicPolicyCheck(
    TopicPolicyAction.EditPost,
    topic,
    currentUser,
  );

  const isSubtopicPermitted =
    isPermittedToCreatePublicSubtopic || topic.privacy !== TopicPrivacy.Public;

  return (
    <>
      {handleNewSubtopic && (
        <MenuItem
          disabled={!isSubtopicPermitted}
          tooltip={
            isSubtopicPermitted
              ? undefined
              : 'Only admins can\ncreate subtopics\nof public topic'
          }
          icon={topicIcon}
          onClick={handleNewSubtopic}
        >
          Create subtopic
        </MenuItem>
      )}
      {canCreatePost && <CreatePostMenuItem topicId={topic.id} />}
      {(handleNewSubtopic || canCreatePost) && <MenuSeparator />}
      <MenuItem icon={memberIcon} onClick={handleViewMembers}>
        View members
      </MenuItem>
      {!isFeatureEnabled('sidebar_section') && (
        <MenuItem icon={favoriteIcon} onClick={() => toggleFavoriteTopic()}>
          {isFavorited ? 'Unfavorite topic' : 'Favorite topic'}
        </MenuItem>
      )}
      {handleEditTopic && (
        <MenuItem icon={editIcon} onClick={handleEditTopic}>
          Edit topic
        </MenuItem>
      )}
      {canEditTopic && topic.childTopics.length > 1 && (
        <MenuItem icon={reorderIcon} onClick={handleReorderSubtopics}>
          Reorder subtopics
        </MenuItem>
      )}
    </>
  );
};

export default TopicItemMenuItems;
